import { useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { FormikProps } from "formik";
import { Employee } from "../types/processTypes";
import { fetchBrokerNameOptions } from "./brokerNameAutocompleteUtils";

interface BrokerNameAutocompleteProps {
  formikProps: FormikProps<Employee>;
}

export const BrokerNameAutocomplete = ({
  formikProps,
}: BrokerNameAutocompleteProps) => {
  const brokerValue = formikProps.values.contactPerson?.kinship ?? "";
  const [inputValue, setInputValue] = useState(brokerValue);
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setInputValue(brokerValue);
  }, [brokerValue]);

  useEffect(() => {
    if (!open) return;

    let active = true;
    setLoading(true);

    const timer = setTimeout(async () => {
      try {
        const nextOptions = await fetchBrokerNameOptions(inputValue);
        if (!active) return;
        setOptions(nextOptions);
      } catch {
        if (active) setOptions([]);
      } finally {
        if (active) setLoading(false);
      }
    }, 200);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [inputValue, open]);

  const setBrokerName = (value: string) => {
    formikProps.setFieldValue("contactPerson.kinship", value.trim());
  };

  return (
    <Autocomplete
      freeSolo
      fullWidth
      size="small"
      open={open}
      openOnFocus
      options={options}
      loading={loading}
      filterOptions={(x) => x}
      autoHighlight
      value={brokerValue}
      inputValue={inputValue}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onInputChange={(_, newValue, reason) => {
        if (reason === "reset") return;
        setInputValue(newValue);
        setBrokerName(newValue);
        if (reason === "input") setOpen(true);
      }}
      onChange={(_, newValue) => {
        const next = typeof newValue === "string" ? newValue : "";
        setInputValue(next);
        setBrokerName(next);
      }}
      onBlur={() => {
        setBrokerName(inputValue);
        setOpen(false);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Broker Name"
          InputLabelProps={{ shrink: true }}
        />
      )}
    />
  );
};
